import React, { useState, useEffect } from 'react';
import {
  Card,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Box,
  Chip,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Slider,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import './LogicComponents.css';

const ConditionBuilder = ({
  condition,
  availableQuestions,
  currentQuestion,
  onChange,
  onDelete,
  showLogicOperator,
  errors,
  index
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState(condition.questionId || '');
  const [operator, setOperator] = useState(condition.operator || 'equals');
  const [value, setValue] = useState(condition.value || '');
  const [logicOperator, setLogicOperator] = useState(condition.logicOperator || 'and');

  // Update parent when local state changes
  useEffect(() => {
    onChange({
      ...condition,
      questionId: selectedQuestion,
      operator,
      value,
      logicOperator: showLogicOperator ? logicOperator : undefined
    });
  }, [selectedQuestion, operator, value, logicOperator]);

  // Get the selected question object
  const selectedQuestionObj = availableQuestions.find(q => q.id === selectedQuestion);

  // Get available operators based on selected question type
  const getOperatorsForQuestionType = (questionType) => {
    const operatorGroups = {
      'short-answer': [
        { value: 'equals', label: 'equals' },
        { value: 'not_equals', label: 'does not equal' },
        { value: 'contains', label: 'contains' },
        { value: 'not_contains', label: 'does not contain' },
        { value: 'starts_with', label: 'starts with' },
        { value: 'ends_with', label: 'ends with' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ],
      'long-answer': [
        { value: 'contains', label: 'contains' },
        { value: 'not_contains', label: 'does not contain' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ],
      'multiple-choice': [
        { value: 'selected', label: 'option is selected' },
        { value: 'not_selected', label: 'option is not selected' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ],
      'checkboxes': [
        { value: 'selected', label: 'option is selected' },
        { value: 'not_selected', label: 'option is not selected' },
        { value: 'any_selected', label: 'any options are selected' },
        { value: 'all_selected', label: 'all options are selected' },
        { value: 'none_selected', label: 'no options are selected' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ],
      'dropdown': [
        { value: 'selected', label: 'option is selected' },
        { value: 'not_selected', label: 'option is not selected' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ],
      'number': [
        { value: 'equals', label: 'equals' },
        { value: 'not_equals', label: 'does not equal' },
        { value: 'greater_than', label: 'is greater than' },
        { value: 'less_than', label: 'is less than' },
        { value: 'between', label: 'is between' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ],
      'rating': [
        { value: 'equals', label: 'equals' },
        { value: 'greater_than', label: 'is greater than' },
        { value: 'less_than', label: 'is less than' },
        { value: 'between', label: 'is between' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ],
      'email': [
        { value: 'equals', label: 'equals' },
        { value: 'contains', label: 'contains' },
        { value: 'not_contains', label: 'does not contain' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ],
      'date': [
        { value: 'equals', label: 'is on' },
        { value: 'not_equals', label: 'is not on' },
        { value: 'greater_than', label: 'is after' },
        { value: 'less_than', label: 'is before' },
        { value: 'between', label: 'is between' },
        { value: 'answered', label: 'is answered' },
        { value: 'not_answered', label: 'is not answered' }
      ]
    };

    return operatorGroups[questionType] || operatorGroups['short-answer'];
  };

  // Render value input based on question type and operator
  const renderValueInput = () => {
    if (['answered', 'not_answered', 'none_selected'].includes(operator)) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No value required for this condition
        </Typography>
      );
    }

    if (!selectedQuestionObj) {
      return (
        <TextField
          fullWidth
          size="small"
          label="Value"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled
          placeholder="Select a question first"
        />
      );
    }

    const questionType = selectedQuestionObj.type;

    // Handle choice-based questions
    if (['multiple-choice', 'checkboxes', 'dropdown'].includes(questionType)) {
      if (['any_selected', 'all_selected'].includes(operator)) {
        // Multiple selection for any/all operators
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Select Options</InputLabel>
            <Select
              multiple
              value={Array.isArray(value) ? value : []}
              onChange={(e) => setValue(e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip key={val} label={val} size="small" />
                  ))}
                </Box>
              )}
            >
              {(selectedQuestionObj.choices || []).map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {choice}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else {
        // Single selection for selected/not_selected operators
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Select Option</InputLabel>
            <Select
              value={value}
              onChange={(e) => setValue(e.target.value)}
            >
              {(selectedQuestionObj.choices || []).map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {choice}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
    }

    // Handle numeric questions
    if (['number', 'rating'].includes(questionType)) {
      if (operator === 'between') {
        const rangeValue = Array.isArray(value) ? value : [0, 10];
        return (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Between {rangeValue[0]} and {rangeValue[1]}
            </Typography>
            <Slider
              value={rangeValue}
              onChange={(e, newValue) => setValue(newValue)}
              valueLabelDisplay="auto"
              min={0}
              max={questionType === 'rating' ? (selectedQuestionObj.config?.max || 5) : 100}
              sx={{ mt: 1 }}
            />
          </Box>
        );
      } else {
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            inputProps={{
              min: 0,
              max: questionType === 'rating' ? (selectedQuestionObj.config?.max || 5) : undefined
            }}
          />
        );
      }
    }

    // Handle date questions
    if (['date', 'datetime'].includes(questionType)) {
      if (operator === 'between') {
        const dateRange = Array.isArray(value) ? value : ['', ''];
        return (
          <Box>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              value={dateRange[0]}
              onChange={(e) => setValue([e.target.value, dateRange[1]])}
              sx={{ mb: 1 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              value={dateRange[1]}
              onChange={(e) => setValue([dateRange[0], e.target.value])}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );
      } else {
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );
      }
    }

    // Default text input
    return (
      <TextField
        fullWidth
        size="small"
        label="Value"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Enter value to compare against"
      />
    );
  };

  const getErrorForField = (field) => {
    return errors[`condition_${index}_${field}`];
  };

  return (
    <Card sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
      {showLogicOperator && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={logicOperator}
              onChange={(e) => setLogicOperator(e.target.value)}
              variant="outlined"
            >
              <MenuItem value="and">AND</MenuItem>
              <MenuItem value="or">OR</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      <Grid container spacing={2} alignItems="flex-start">
        {/* Question Selection */}
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small" error={!!getErrorForField('question')}>
            <InputLabel>Question</InputLabel>
            <Select
              value={selectedQuestion}
              onChange={(e) => {
                setSelectedQuestion(e.target.value);
                // Reset operator and value when question changes
                setOperator('equals');
                setValue('');
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {availableQuestions.map((q, index) => {
                const questionText = q.question_text || q.title || `Question ${index + 1}`;
                return (
                  <MenuItem key={q.id || index} value={q.id || `temp-${index}`}>
                    <Tooltip title={questionText} placement="top">
                      <Box sx={{ width: '100%', overflow: 'hidden' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '250px'
                          }}
                        >
                          {questionText}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            textTransform: 'capitalize',
                            fontSize: '0.75rem'
                          }}
                        >
                          {q.type?.replace('-', ' ')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </MenuItem>
                );
              })}
            </Select>
            {getErrorForField('question') && (
              <Typography variant="caption" color="error">
                {getErrorForField('question')}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Operator Selection */}
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small" error={!!getErrorForField('operator')}>
            <InputLabel>Condition</InputLabel>
            <Select
              value={operator}
              onChange={(e) => {
                setOperator(e.target.value);
                // Reset value when operator changes
                setValue('');
              }}
            >
              {getOperatorsForQuestionType(selectedQuestionObj?.type).map(op => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
            {getErrorForField('operator') && (
              <Typography variant="caption" color="error">
                {getErrorForField('operator')}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Value Input */}
        <Grid item xs={12} sm={4}>
          <Box sx={{ minHeight: 40 }}>
            {renderValueInput()}
            {getErrorForField('value') && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {getErrorForField('value')}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Delete Button */}
        <Grid item xs={12} sm={1}>
          <IconButton 
            onClick={onDelete} 
            size="small" 
            color="error"
            sx={{ ml: 'auto' }}
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>

      {/* Condition Preview */}
      {selectedQuestion && operator && (
        <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            Preview: When "{(selectedQuestionObj?.question_text || selectedQuestionObj?.title || '').substring(0, 50)}{(selectedQuestionObj?.question_text || selectedQuestionObj?.title || '').length > 50 ? '...' : ''}" {
              getOperatorsForQuestionType(selectedQuestionObj?.type).find(op => op.value === operator)?.label
            } {
              !['answered', 'not_answered', 'none_selected'].includes(operator) && 
              (Array.isArray(value) ? value.join(' and ') : `"${value}"`)
            }
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default ConditionBuilder;